/**
 * @module storage
 * @category API / Storage
 *
 * Authentication session storage.
 *
 * The access token returned by token-issuing BFF endpoints is kept in
 * `sessionStorage` (scoped to the tab, cleared when it closes) together with
 * its expiry, and sent by the API client as `Authorization: Bearer`.
 * `refreshToken` is never stored.
 *
 * The token that authorizes the onboarding password step (invitation /
 * first access) is kept **in memory only** and is not a login session.
 */

const STORAGE_KEY = 'auth_session';

/**
 * Token data returned by token-issuing BFF endpoints.
 *
 * @property accessToken - JWT sent as `Authorization: Bearer`
 * @property expiresIn   - Token lifetime: relative seconds **or** absolute Unix epoch timestamp
 * @property tokenType   - Token scheme (default `"Bearer"`)
 * @property scope       - Optional list of OAuth permission scopes granted to the token
 */
export interface AuthSessionData {
  accessToken: string;
  expiresIn: string | number;
  tokenType?: string;
  scope?: string[];
}

/**
 * Session metadata as exposed to callers, with `expiresIn` recomputed as the
 * number of seconds remaining.
 */
export interface AuthSession {
  expiresIn: number;
  tokenType: string;
  scope?: string[];
}

interface SessionState {
  accessToken: string;
  /** Absolute expiry, epoch milliseconds */
  expiresAt: number;
  tokenType: string;
  scope?: string[];
}

let onboardingToken: string | null = null;

const readSession = (): SessionState | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SessionState>;
    if (
      typeof parsed.accessToken !== 'string' ||
      !parsed.accessToken ||
      typeof parsed.expiresAt !== 'number'
    ) {
      return null;
    }
    return {
      accessToken: parsed.accessToken,
      expiresAt: parsed.expiresAt,
      tokenType: parsed.tokenType ?? 'Bearer',
      scope: parsed.scope,
    };
  } catch {
    return null;
  }
};

/**
 * Normalizes `expiresIn` from API responses to a **seconds-from-now** TTL.
 *
 * The API may return a relative duration in seconds (e.g. `3600`) or an
 * absolute Unix epoch timestamp in seconds (e.g. `"1776436734"`). Values
 * above `1_000_000_000` (Sep 2001+) are treated as epoch timestamps.
 *
 * @returns Seconds until expiry, clamped to `0` for invalid/past values
 */
const normalizeExpiresIn = (expiresIn: string | number): number => {
  const value = typeof expiresIn === 'string' ? Number(expiresIn) : expiresIn;
  if (Number.isNaN(value) || value <= 0) return 0;

  if (value > 1_000_000_000) {
    return Math.max(0, Math.floor(value - Date.now() / 1000));
  }
  return value;
};

/**
 * Stores a freshly issued token.
 *
 * @example
 * ```ts
 * setAuthSession({ accessToken, expiresIn: 3600, tokenType: 'Bearer' });
 * ```
 */
export const setAuthSession = (data: AuthSessionData): void => {
  const ttl = normalizeExpiresIn(data.expiresIn);
  const state: SessionState = {
    accessToken: data.accessToken,
    expiresAt: Date.now() + ttl * 1000,
    tokenType: data.tokenType ?? 'Bearer',
    scope: data.scope,
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage unavailable (e.g. quota / privacy mode): session is not kept
  }
};

/**
 * Whether a stored session is present (regardless of expiry).
 */
export const hasAuthSession = (): boolean => readSession() !== null;

/**
 * Whether the session is absent or past its expiry. Client-side guard only —
 * the BFF still validates the token on every request.
 */
export const isTokenExpired = (): boolean => {
  const session = readSession();
  if (!session) return true;
  return Date.now() >= session.expiresAt;
};

/**
 * Removes the stored session.
 */
export const clearAuthSession = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: nothing was stored
  }
};

/**
 * Returns the access token of the current session, or `null` when
 * absent/expired.
 */
export const getAccessToken = (): string | null => {
  const session = readSession();
  if (!session || Date.now() >= session.expiresAt) return null;
  return session.accessToken;
};

/**
 * Returns the current session metadata, or `null` when absent/expired
 * (an expired session is discarded).
 */
export const getAuthSession = (): AuthSession | null => {
  const session = readSession();
  if (!session) return null;
  if (Date.now() >= session.expiresAt) {
    clearAuthSession();
    return null;
  }
  return {
    expiresIn: Math.floor((session.expiresAt - Date.now()) / 1000),
    tokenType: session.tokenType,
    scope: session.scope,
  };
};

/**
 * Returns whether the current session exists and has not expired.
 *
 * @example
 * ```ts
 * if (!isAuthenticated()) return <Navigate to="/login" />;
 * ```
 */
export const isAuthenticated = (): boolean => {
  return hasAuthSession() && !isTokenExpired();
};

/** Keeps the onboarding (password step) token in memory. */
export const setOnboardingToken = (token: string): void => {
  onboardingToken = token;
};

export const getOnboardingToken = (): string | null => onboardingToken;

export const clearOnboardingToken = (): void => {
  onboardingToken = null;
};
